/**
 * filterTree — the pure model + wire round-trip for the visual boolean-filter
 * builder (ADR-0055 S4). The UI edits an ergonomic tree (a GROUP has a combinator
 * `and|or` + a `negate` flag + children; a LEAF is `{column, op, value}`); it
 * serialises to the CANONICAL wire shape `_build_where` / `TrayFilterNode` accepts:
 * a leaf `{column, op, value}` or a connective `{and:[…]}` / `{or:[…]}` / `{not:…}`.
 *
 * The stored `spec.filters` is ALWAYS a LIST (it rides `ResourceSpec.filters:
 * list[dict]`), so `rootToSpec` never emits a bare object — a non-AND / negated
 * root is wrapped as a single-element list `[{or:[…]}]` (the backend list = implicit
 * AND, so a 1-element list evaluates its one node). Pure — no React, unit-tested.
 */

export type FilterLeaf = { type: "leaf"; column: string; op: string; value?: unknown };
export type FilterGroup = {
  type: "group";
  combinator: "and" | "or";
  negate: boolean;
  children: FilterNode[];
};
export type FilterNode = FilterLeaf | FilterGroup;

/** The canonical wire node (mirror of the DTS `TrayFilterNode`). */
export type WireLeaf = { column: string; op: string; value?: unknown };
export type WireNode =
  | WireLeaf
  | { and: WireNode[] }
  | { or: WireNode[] }
  | { not: WireNode };

/** Ops that take NO value (`is_null`/`not_null`) and ops whose value is a LIST
 *  (`in`/`has` — a comma string in the UI, split to an array at wire time). */
export const NO_VALUE_OPS = new Set(["is_null", "not_null"]);
export const ARRAY_OPS = new Set(["in", "has"]);

/** The designer op set — the canonical `_build_where` operators (IT labels). */
export const FILTER_TREE_OPS: { value: string; label: string }[] = [
  { value: "eq", label: "=" },
  { value: "ne", label: "≠" },
  { value: "gt", label: ">" },
  { value: "gte", label: "≥" },
  { value: "lt", label: "<" },
  { value: "lte", label: "≤" },
  { value: "contains", label: "contiene" },
  { value: "in", label: "in (lista)" },
  { value: "has", label: "ha (relazione)" },
  { value: "is_null", label: "è vuoto" },
  { value: "not_null", label: "non è vuoto" },
];

export const newLeaf = (column = ""): FilterLeaf => ({ type: "leaf", column, op: "eq", value: "" });
export const newGroup = (combinator: "and" | "or" = "and"): FilterGroup => ({
  type: "group",
  combinator,
  negate: false,
  children: [],
});
export const emptyRoot = (): FilterGroup => newGroup("and");

/** A leaf is COMPLETE when it can produce a real predicate: a column + (a no-value
 *  op OR a non-empty value). Incomplete leaves are dropped on serialise. */
export function isLeafComplete(leaf: FilterLeaf): boolean {
  if (!leaf.column) return false;
  if (NO_VALUE_OPS.has(leaf.op)) return true;
  const v = leaf.value;
  if (Array.isArray(v)) return v.length > 0;
  return v !== undefined && v !== null && String(v) !== "";
}

/** Count the real (complete) leaves under a node — drives the "N condizioni" badge. */
export function countLeaves(node: FilterNode): number {
  if (node.type === "leaf") return isLeafComplete(node) ? 1 : 0;
  return node.children.reduce((n, c) => n + countLeaves(c), 0);
}

function leafToWire(leaf: FilterLeaf): WireLeaf {
  const w: WireLeaf = { column: leaf.column, op: leaf.op };
  if (!NO_VALUE_OPS.has(leaf.op)) {
    let v = leaf.value;
    // `in`/`has` want an ARRAY — a comma string in the UI becomes `["a","b"]`.
    if (ARRAY_OPS.has(leaf.op) && typeof v === "string") {
      v = v.split(",").map((s) => s.trim()).filter((s) => s !== "");
    }
    w.value = v;
  }
  return w;
}

/** UI node → wire node (a group becomes `{and|or:[…]}`, wrapped in `{not:…}` when negated). */
export function nodeToWire(node: FilterNode): WireNode {
  if (node.type === "leaf") return leafToWire(node);
  const inner = { [node.combinator]: node.children.map(nodeToWire) } as WireNode;
  return node.negate ? { not: inner } : inner;
}

/** Wire node → UI node (the inverse; a `{not:<leaf>}` becomes a negated AND group
 *  wrapping the leaf so the UI can always render a negation as a group toggle). */
export function wireToNode(w: unknown): FilterNode {
  if (w && typeof w === "object") {
    const o = w as Record<string, unknown>;
    if ("not" in o) {
      const inner = wireToNode(o.not);
      if (inner.type === "group") return { ...inner, negate: !inner.negate };
      return { type: "group", combinator: "and", negate: true, children: [inner] };
    }
    if ("and" in o) return { type: "group", combinator: "and", negate: false, children: asArray(o.and).map(wireToNode) };
    if ("or" in o) return { type: "group", combinator: "or", negate: false, children: asArray(o.or).map(wireToNode) };
    if ("column" in o) {
      let value = o.value;
      // Array value (`in`/`has`) → a comma string for the UI input.
      if (Array.isArray(value)) value = value.join(", ");
      return { type: "leaf", column: String(o.column ?? ""), op: String(o.op ?? "eq"), value };
    }
  }
  return newLeaf("");
}

const asArray = (v: unknown): unknown[] => (Array.isArray(v) ? v : []);

/** Drop incomplete leaves + empty groups, recursively (never mutates the input). */
export function normalizeGroup(g: FilterGroup): FilterGroup {
  const children = g.children
    .map((c) => (c.type === "group" ? normalizeGroup(c) : c))
    .filter((c) => (c.type === "group" ? c.children.length > 0 : isLeafComplete(c)));
  return { ...g, children };
}

/** Stored `spec.filters` (a list = implicit AND, a single node, or empty) → the
 *  editable ROOT group. A single non-group node is wrapped in an AND group so the
 *  builder always edits a group. */
export function specToRoot(filters: unknown): FilterGroup {
  if (Array.isArray(filters)) {
    return { type: "group", combinator: "and", negate: false, children: filters.map(wireToNode) };
  }
  if (filters && typeof filters === "object") {
    const node = wireToNode(filters);
    return node.type === "group" ? node : { type: "group", combinator: "and", negate: false, children: [node] };
  }
  return emptyRoot();
}

/** The ROOT group → `spec.filters` — ALWAYS a LIST (ResourceSpec.filters is a list).
 *  A plain non-negated AND root flattens to a list of its children (the idiomatic
 *  implicit-AND shape); anything else rides as a single-element list `[{or:[…]}]`. */
export function rootToSpec(root: FilterGroup): WireNode[] {
  const g = normalizeGroup(root);
  if (!g.children.length) return [];
  if (g.combinator === "and" && !g.negate) return g.children.map(nodeToWire);
  return [nodeToWire(g)];
}
